import {
  CUTOFF_PRESETS,
  dropLabel,
  ONCE_OPENS_OPTIONS,
  OPENS_OPTIONS,
  questionSteps,
  RHYTHMS,
  stepValid,
  WEEKDAYS,
  WINDOW_PRESETS
} from '@/config/dropWizardSteps'
import { formatClock, formatLongDate } from '@/helpers/dropWizardDates'

export { railRows } from '@/helpers/wizardRail'

const join = items =>
  items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items[0] ?? ''
const lowerFirst = text => text.charAt(0).toLowerCase() + text.slice(1)

export const dayNames = data =>
  [...data.weekdays]
    .sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
    .map(value => WEEKDAYS.find(day => day.value === value).long)

export const daysText = data => {
  if (data.rhythm === 'once') return data.date ? formatLongDate(data.date) : 'Pick a date'
  if (!data.weekdays.length) return 'Pick a day'
  const names = dayNames(data)
  return names.length === 5 && [1, 2, 3, 4, 5].every(day => data.weekdays.includes(day))
    ? 'Monday to Friday'
    : join(names)
}

export const windowText = arrival => {
  const preset = WINDOW_PRESETS.find(option => option.value === arrival.window)
  const [start, end] = !preset || preset.value === 'custom' ? [arrival.start, arrival.end] : [preset.start, preset.end]
  return `${formatClock(start)} to ${formatClock(end)}`
}

export const cutoffText = ordering => {
  const preset = CUTOFF_PRESETS.find(option => option.value === ordering.cutoff)
  if (preset && preset.value !== 'custom') return lowerFirst(preset.title).replace(', ', ' at ')
  const days = ordering.closesDaysBefore
  const when = days === 0 ? 'the morning of' : days === 1 ? 'the day before' : `${days} days before`
  return `${when} at ${formatClock(ordering.closesTime)}`
}

export const opensText = ordering => {
  if (ordering.opens === 'previous') return 'when the last one closes'
  if (ordering.opens === 'now') return 'now'
  const option = [...OPENS_OPTIONS, ...ONCE_OPENS_OPTIONS].find(item => item.value === ordering.opens)
  return option ? lowerFirst(option.name) : 'now'
}

const rhythmText = data => RHYTHMS.find(option => option.value === data.rhythm)?.title ?? 'Pick a rhythm'

const methodFor = (data, methods) =>
  data.fulfillment.newMethod
    ? { name: data.fulfillment.newMethod.name.trim() || 'a new method', type: data.fulfillment.newMethod.type }
    : methods.find(method => method.id === String(data.fulfillment.methodId))

const arrivalDay = data => (data.arrival.offsetDays === 1 ? 'next day' : 'same day')

export const audienceText = (data, tiers) => {
  const { audience } = data
  if (audience.kind === 'everyone') return 'Everyone'
  if (audience.kind === 'tiers')
    return (
      join(audience.tierAudienceIds.map(id => tiers.find(tier => tier.audienceId === id)?.name ?? 'a membership')) ||
      'Pick a membership'
    )
  if (audience.kind === 'lists') return join(audience.lists.map(list => list.name)) || 'Pick a list'
  return 'Who can order'
}

const remindersText = data =>
  data.reminders.length
    ? `${data.reminders.length} ${data.reminders.length === 1 ? 'reminder' : 'reminders'}`
    : 'no reminders'

const AUTOFILL_SHORT = { refill: 'last order', category: 'last order by category', cart: 'a set cart' }
const AUTOFILL_SENTENCE = {
  refill: 'Repeat customers start with their last order in the cart.',
  category: 'Repeat customers start with the same number of items per category as last time, from this menu.',
  cart: 'Every customer starts with the cart you built.'
}
const autofillShort = payment => AUTOFILL_SHORT[payment.autofillKind] ?? 'last order'
export const autofillSentence = payment =>
  payment.autofill ? AUTOFILL_SENTENCE[payment.autofillKind] ?? AUTOFILL_SENTENCE.refill : ''

const paymentText = data => {
  if (typeof data.payment.autocharge !== 'boolean') return 'How customers pay'
  return `${data.payment.autocharge ? 'Autocharge at cutoff' : 'Customers check out'}${data.payment.autofill ? ` · fill carts from ${autofillShort(data.payment)}` : ''}`
}

const VALUES = {
  what: (data, { stores }) => {
    const store = stores.find(item => item.id === String(data.storeId))
    if (!data.name.trim() && !store) return null
    return [data.name.trim(), store?.name].filter(Boolean).join(' · ')
  },
  often: data => (data.rhythm ? rhythmText(data) : null),
  day: data => ((data.rhythm === 'once' ? data.date : data.weekdays.length) ? daysText(data) : null),
  get: (data, { methods }) => {
    const method = methodFor(data, methods)
    return method ? `${method.name}, ${arrivalDay(data)}, ${windowText(data.arrival)}` : null
  },
  close: data =>
    `Opens ${opensText(data.ordering)}. Closes ${cutoffText(data.ordering)}${data.ordering.limitOn ? `. Up to ${data.ordering.limit} orders` : ''}`,
  who: (data, { tiers }) => (data.audience.kind ? `${audienceText(data, tiers)} · ${remindersText(data)}` : null),
  pay: data => (typeof data.payment.autocharge === 'boolean' ? paymentText(data) : null)
}

const PROMPTS = {
  what: 'Name and store',
  often: 'How often',
  day: 'Pick a day',
  get: 'How customers get it',
  close: 'When ordering closes',
  who: 'Who can order',
  pay: 'How customers pay'
}

export const describeSetup = (data, context) => {
  const rows = questionSteps().map(step => {
    const value = VALUES[step.key](data, context)
    return {
      key: step.key,
      label: step.label,
      value: value ?? PROMPTS[step.key],
      set: value !== null,
      valid: stepValid(step.key, data)
    }
  })
  return { rows, sentence: sentence(data, context) }
}

export const sentence = (data, { stores, methods }) => {
  const store = stores.find(item => item.id === String(data.storeId))
  const method = methodFor(data, methods)
  const when =
    data.rhythm === 'once'
      ? `on ${data.date ? formatLongDate(data.date) : 'one date'}`
      : `${data.rhythm === 'biweekly' ? 'every other ' : data.rhythm === 'four_weekly' ? 'every fourth ' : 'every '}${data.weekdays.length ? daysText(data) : 'week'}`
  const get = method?.type === 'pickup' ? `Customers pick it up at ${method.name}` : `You deliver it to their door`
  const limit = data.ordering.limitOn ? `, or once ${data.ordering.limit} orders are in` : ''
  const pay = data.payment.autocharge
    ? 'Saved carts are charged automatically when ordering closes.'
    : 'Customers check out and pay themselves.'
  return `You make ${dropLabel(data)} ${when} for ${store?.name ?? 'your store'}. ${get} the ${arrivalDay(data)} between ${windowText(data.arrival).replace(' to ', ' and ')}. Ordering opens ${opensText(data.ordering)} and closes ${cutoffText(data.ordering)}${limit}. ${pay}`
}

export const zoneText = zone => {
  if (zone.kind === 'anywhere') return 'anywhere'
  if (zone.kind === 'map') return 'to a drawn area'
  if (zone.kind === 'states') return `to ${zone.states.length} ${zone.states.length === 1 ? 'state' : 'states'}`
  const zips = zone.zipCodes.length
  return `to ${zips} zip ${zips === 1 ? 'code' : 'codes'}`
}

const newMethodItem = method => {
  const name = method.name.trim() || 'a new method'
  const text =
    method.type === 'pickup'
      ? `Pickup at ${method.address.address1.trim()}, ${method.address.city.trim()}.`
      : `Delivery ${zoneText(method.zone)}.`
  return { title: `A new fulfillment method: ${name}`, text }
}

const remindersItem = (count, sending) => {
  if (!count) return { title: 'No reminders', text: 'You can add reminders from the drop page later.' }
  if (!sending)
    return {
      title: 'No reminders will send',
      text: 'None of the reminders has a send time for these dates. Change when they go out on the Who step.'
    }
  const unsent = count - sending
  return {
    title: `${sending} text ${sending === 1 ? 'reminder' : 'reminders'}`,
    text: `Sent for every date, using the messages you wrote.${unsent ? ` ${unsent} more ${unsent === 1 ? 'reminder has' : 'reminders have'} no send time and will not go out.` : ''}`
  }
}

export const whatWeSetUp = (
  data,
  { method, audienceText: audience, dateCount, storeName, sendingCount = data.reminders.length }
) => {
  const repeat =
    data.rhythm === 'once'
      ? 'Does not repeat.'
      : `${RHYTHMS.find(option => option.value === data.rhythm).title.replace('Every', 'Repeats every')}. Bottle keeps six months of dates ahead and adds more as time passes.`
  return [
    ...(data.fulfillment.newMethod ? [newMethodItem(data.fulfillment.newMethod)] : []),
    {
      title: `A fulfillment slot on ${method?.name ?? 'your method'}`,
      text: `Production on ${daysText(data)}, arrival ${windowText(data.arrival)} the ${arrivalDay(data)}, order cutoff ${cutoffText(data.ordering)}, ${data.ordering.limitOn ? `limited to ${data.ordering.limit} orders` : 'no order limit'}. ${repeat}`
    },
    {
      title: `A drop called ${dropLabel(data)}`,
      text: `In ${storeName}, open to ${audience}, using the ${dateCount} production ${dateCount === 1 ? 'day' : 'days'} above so every date has a slot.`
    },
    remindersItem(data.reminders.length, sendingCount),
    {
      title: data.payment.autocharge ? 'Autocharge at each cutoff' : 'No autocharge',
      text: `${data.payment.autocharge ? 'Every cart with a card on file is charged when ordering closes.' : 'Customers pay at checkout.'}${data.payment.autofill ? ` ${autofillSentence(data.payment)}` : ''}`
    }
  ]
}
